import PropTypes from 'prop-types';
import { Navigate } from 'react-router-dom';
// hooks
import useAuth from '../hooks/useAuth';
import {
  AUTH_CALLBACK_AT_LOAD,
  newPasswordPathFor
} from '../contexts/JWTContext';
// pages
import Login from '../pages/auth/Login';
import LinguistLogin from '../pages/auth/linguistAuth/Login';
import VerifyEmail from 'src/pages/linguist/VerifyEmail';
import ClientLogin from '../pages/auth/clientAuth/Login';

// components
import LoadingScreen from '../components/LoadingScreen';
import MediationRedirectGate from './MediationRedirectGate';
import SessionPending from './SessionPending';
import { AUTH_PORTALS } from '../layouts/auth/portals';

// ----------------------------------------------------------------------

// The portal whose sign-in this guard shows to a visitor who is not signed in.
const PORTAL_OF_TYPE = {
  user: AUTH_PORTALS.intranet,
  Client: AUTH_PORTALS.client
};
const portalFor = (userType) =>
  PORTAL_OF_TYPE[userType[0]] || AUTH_PORTALS.vendor;

AuthGuard.propTypes = {
  children: PropTypes.node,
  userType: PropTypes.arrayOf(PropTypes.string)
};

export default function AuthGuard({ children, userType }) {
  const { isAuthenticated, isInitialized, type, user, passwordRecovery } =
    useAuth();
  const userHasRequiredType = userType.indexOf(type) !== -1;
  if (!isInitialized) {
    // Back from Microsoft on the page the button was pressed on — a deep link
    // signed in from the sign-in this guard showed: the sky it was left
    // from, not the dashboard's loader. A stored session being restored is a
    // dashboard reload, and keeps the loader it always had.
    return AUTH_CALLBACK_AT_LOAD ? (
      <SessionPending portal={portalFor(userType)} />
    ) : (
      <LoadingScreen />
    );
  }

  // A session minted by a recovery or invite link may set a password and do
  // nothing else — see JWTContext. It goes to the form of whichever portal
  // owns the account, not this guard's, so a client's link never lands on a
  // staff login.
  if (isAuthenticated && passwordRecovery) {
    return <Navigate to={newPasswordPathFor(type)} replace />;
  }

  if (isAuthenticated && userHasRequiredType) {
    // Legacy 6-digit email check. Nothing sets 'unverified' any more (Supabase
    // confirms addresses for new registrations), but ~520 pre-2024 linguist
    // records still carry it and the resolvers behind this page are live.
    if (user?.profileStatus === 'unverified' && user?.type === 'Linguist') {
      return <VerifyEmail />;
    }
    return <>{children}</>;
  }

  if (userType[0] === 'user') {
    return <Login />;
  }
  if (userType[0] === 'Client') {
    return <ClientLogin />;
  }
  // Un mediatore non supererà mai questa login (identità Supabase, nessuna
  // password Universe). Se il link che sta aprendo è un incarico di
  // mediazione lo mandiamo sul suo portale invece di lasciarlo bloccato;
  // per tutti gli altri vendor il gate è trasparente. Vedi
  // MediationRedirectGate.
  return (
    <MediationRedirectGate>
      <LinguistLogin />
    </MediationRedirectGate>
  );
}
